/**
 * Ekya Hero Carousel — smooth fade, autoplay, swipe, dots, arrows
 */
class EkyaHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.ekya-hero__slide')];
    if (this.slides.length <= 1) return;

    this.current = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = parseInt(this.dataset.speed, 10) || 5000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;
    this.isTransitioning = false;

    this.prevBtn = this.querySelector('.ekya-hero__arrow--prev');
    this.nextBtn = this.querySelector('.ekya-hero__arrow--next');
    this.dots = [...this.querySelectorAll('.ekya-hero__dot')];
    this.progress = this.querySelector('.ekya-hero__progress-bar');

    this.prevBtn?.addEventListener('click', () => this.goTo(this.current - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.current + 1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.slideTo, 10)));
    });

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());

    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.pause() : this.play();
    });

    this.#initSwipe();

    if (this.autoplay && !this.reducedMotion) this.play();
  }

  disconnectedCallback() {
    this.pause();
  }

  goTo(index) {
    if (this.isTransitioning) return;

    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    if (next === this.current) return;

    this.isTransitioning = true;

    const outgoing = this.slides[this.current];
    const incoming = this.slides[next];

    outgoing.classList.remove('is-active');
    outgoing.setAttribute('aria-hidden', 'true');

    incoming.classList.add('is-active');
    incoming.setAttribute('aria-hidden', 'false');

    this.dots.forEach((dot, i) => {
      const active = i === next;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.current = next;
    this.#restartProgress();

    window.setTimeout(() => {
      this.isTransitioning = false;
    }, 900);

    this.resetTimer();
  }

  next() {
    this.goTo(this.current + 1);
  }

  play() {
    if (!this.autoplay || this.reducedMotion || this.slides.length <= 1) return;
    this.pause();
    this.#restartProgress();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  pause() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.progress) {
      this.progress.style.animationPlayState = 'paused';
    }
  }

  resetTimer() {
    if (this.autoplay && !this.reducedMotion) {
      this.pause();
      this.play();
    }
  }

  #restartProgress() {
    if (!this.progress || this.reducedMotion) return;
    this.progress.style.animation = 'none';
    void this.progress.offsetWidth;
    this.progress.style.animation = '';
    this.progress.style.animationPlayState = 'running';
  }

  #initSwipe() {
    let startX = 0;
    let startY = 0;

    this.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) this.goTo(this.current + 1);
        else this.goTo(this.current - 1);
      },
      { passive: true }
    );
  }
}

if (!customElements.get('ekya-hero-carousel')) {
  customElements.define('ekya-hero-carousel', EkyaHeroCarousel);
}
